import React, { useState, useEffect } from "react";
import { adminCreateBlog, adminUpdateBlog } from "../api/admin";
import ImageUpload from "./ImageUpload";
import RichTextEditor from "./RichTextEditor";
import { toast } from "react-toastify";

const BlogForm = ({ blog, onSuccess, onCancel }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [blogUrl, setBlogUrl] = useState("");
  const [images, setImages] = useState([]); // Array of { url, file }
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [schemaMarkup, setSchemaMarkup] = useState("");

  /* Populate fields when editing */
  useEffect(() => {
    if (blog) {
      setTitle(blog.title || "");
      setContent(blog.content || "");
      setBlogUrl(blog.blog_url || "");

      const blogImage = Array.isArray(blog.image) ? blog.image[0] : blog.image || "";
      const imageUrl = (typeof blogImage === 'object' && blogImage !== null) ? blogImage.url : blogImage;
      setImages(imageUrl ? [{ url: imageUrl, file: null }] : []);

      setMetaTitle(blog.blog_meta_title || "");
      setMetaDescription(blog.blog_meta_description || "");
      setMetaKeywords((blog.blog_meta_keywords || []).join(", "));
      setSchemaMarkup(blog.blog_schema_markup || "");
    }
  }, [blog]);

  const handleAddImages = (files) => {
    const file = files[0];
    if (file) {
      setImages([{
        url: URL.createObjectURL(file),
        file: file
      }]);
    }
  };

  const removeImage = () => {
    setImages(prev => {
      if (prev.length > 0 && prev[0].file) {
        URL.revokeObjectURL(prev[0].url);
      }
      return [];
    });
  };

  /* Removed auto-generate URL effect because it was overwriting the URL input */

  const resetForm = () => {
    setTitle("");
    setContent("");
    setBlogUrl("");
    setImages([]);
    setMetaTitle("");
    setMetaDescription("");
    setMetaKeywords("");
    setSchemaMarkup("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("blog_url", blogUrl);

    // Handle image
    if (images.length > 0) {
      if (images[0].file) {
        formData.append("images", images[0].file);
      }
      // Do not append string URL for existing images to avoid Pydantic value_error
    }

    formData.append("blog_meta_title", metaTitle || "");
    formData.append("blog_meta_description", metaDescription || "");

    const keywordsArray = metaKeywords
      ? metaKeywords.split(",").map(k => k.trim())
      : [];
    formData.append("blog_meta_keywords", JSON.stringify(keywordsArray));
    formData.append("blog_schema_markup", schemaMarkup || "");

    try {
      if (blog) {
        await adminUpdateBlog(blog.id || blog._id, formData);
        toast.success("Blog updated successfully! ✨");
      } else {
        await adminCreateBlog(formData);
        toast.success("Blog published successfully! 📰");
      }
      onSuccess();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save blog. ❌");
    }
  };

  const handleAutoResize = (e) => {
    const target = e.target || e;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const textareas = document.querySelectorAll('.admin-form textarea:not(.rte-content)');
      textareas.forEach(ta => handleAutoResize(ta));
    }, 100);
    return () => clearTimeout(timer);
  }, [blog]);

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <div className="form-body">
        {/* Section 1: Featured Image */}
        <div className="form-section-title">🖼️ Featured Image</div>
        <div className="form-group full-width">
          <ImageUpload
            images={images}
            onAdd={handleAddImages}
            onRemove={removeImage}
            multiple={false}
            id="blog-image-upload"
          />
        </div>

        {/* Section 2: Blog Details */}
        <div className="form-section-title">📝 Blog Details</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Blog Title <span className="required-star">*</span></label>
            <input
              type="text"
              placeholder="Enter blog title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Blog URL <span className="required-star">*</span></label>
            <input
              type="text"
              placeholder="slug-for-blog"
              value={blogUrl}
              onChange={(e) => setBlogUrl(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label>Blog Content <span className="required-star">*</span></label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="Write your blog content here..."
          />
        </div>

        {/* Section 3: SEO & Metadata */}
        <div className="form-section-title">🔍 SEO & Metadata</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Meta Title</label>
            <textarea
              placeholder="SEO Title"
              value={metaTitle}
              onChange={(e) => { setMetaTitle(e.target.value); handleAutoResize(e); }}
              rows={1}
            />
          </div>

          <div className="form-group">
            <label>Meta Keywords</label>
            <textarea
              placeholder="keyword1, keyword2"
              value={metaKeywords}
              onChange={(e) => { setMetaKeywords(e.target.value); handleAutoResize(e); }}
              rows={1}
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label>Meta Description</label>
          <textarea
            placeholder="SEO description..."
            value={metaDescription}
            onChange={(e) => { setMetaDescription(e.target.value); handleAutoResize(e); }}
            rows={2}
          />
        </div>

        <div className="form-group full-width">
          <label>Schema Markup</label>
          <textarea
            placeholder="Paste JSON-LD schema markup here"
            value={schemaMarkup}
            onChange={(e) => { setSchemaMarkup(e.target.value); handleAutoResize(e); }}
            rows={3}
          />
        </div>

        <div className="form-footer">
          <button type="submit" className="btn-submit">
            {blog ? "Update Blog" : "Save Blog"}
          </button>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default BlogForm;
